import { useEffect, useRef, useState } from 'react'

import { useSession } from 'next-auth/react'

import { A11y, Keyboard, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import CategoryTextNodes from '@data/i18n/category.json'

import { getCustomerFavorites } from '@lib/operations/customer-favorites'
import type { ProductCard as ProductCardType } from '@lib/types'

import ProductCard from '@components/products/ProductOrCollectionCard'

type Props = {
	title: string
	products: ProductCardType[]
}

export default function ProductsCarousel({ title, products }: Props) {
	const { data: session } = useSession()

	const prevButton = useRef<HTMLButtonElement>(null)
	const nextButton = useRef<HTMLButtonElement>(null)

	const [favorites, setFavorites] = useState<string[]>([])

	useEffect(() => {
		const getFavorites = async () => {
			const customerFavorites: string[] =
				(await getCustomerFavorites((session as any)?.shopifyAccessToken as string)) || []
			setFavorites(customerFavorites)
		}

		getFavorites()
	}, [(session as any)?.shopifyAccessToken])

	return (
		<section className="products-carousel section">
			<div className="container flex flex-col">
				{title && <p className="text-3xl title mb-4">{title}</p>}

				{products.length > 0 ? (
					<div className="relative">
						<Swiper
							modules={[A11y, Keyboard, Navigation]}
							navigation={{ nextEl: nextButton.current, prevEl: prevButton.current }}
							keyboard
							loop
							slidesPerView="auto"
							spaceBetween={16}
							breakpoints={{
								375: {
									slidesPerView: 2
								},
								768: {
									slidesPerView: 3,
									spaceBetween: 24
								},
								1024: {
									slidesPerView: 4
								}
							}}
						>
							{products.map((product: ProductCardType) => (
								<SwiperSlide key={product.id}>
									<ProductCard
										id={product.id}
										name={product.name}
										image={product.image}
										price={product.price}
										handle={product.handle}
										avgRating={product.avgRating}
										numReviews={product.numReviews}
										vendor={product.vendor}
										isFavorite={favorites.includes(product.id)}
										withoutShadow
									/>
								</SwiperSlide>
							))}
						</Swiper>
						{/* A11Y: Added aria-label for screen readers since buttons only contain icons */}
						<button type="button" ref={prevButton} className="swiper-button swiper-button-prev" aria-label="Previous products">
							<i className="fa-light fa-chevron-left" aria-hidden="true" />
						</button>
						<button type="button" ref={nextButton} className="swiper-button swiper-button-next" aria-label="Next products">
							<i className="fa-light fa-chevron-right" aria-hidden="true" />
						</button>
					</div>
				) : (
					<div className="flex flex-col items-center justify-center h-96">
						<i className="text-gray-300 fa-light fa-filter-slash text-5xl mb-6" />
						<span className="text-sm text-gray-400 font-medium">
							{CategoryTextNodes.products.empty}
						</span>
					</div>
				)}
			</div>
		</section>
	)
}
